import React from 'react';
import Card from '@components/UI/Card';
import {
    calculateUserPortfolioTradePerformance,
    isEvaluatedBacktestTrade,
    SimulateBacktestTrades,
    type UserPortfolioTradePerformance
} from '@einhaus/project314-shared';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { getDate } from '@einhaus/meteoric-helpers';
import EntityLink from '@components/Entity/EntityLink';
import type {
    UserPortfolioTrade,
    Entity,
    OptionBuyingStrategy,
    AssetOption,
    EntityOptionMetadata,
    TradingStrategy,
    TradingStrategyBacktestTrade
} from '@einhaus/project314-types';
import { getEntityOptionLink } from '@/utils/entityHelpers.ts';
import { Link, useLocation } from 'react-router-dom';
import { formatPositionSide, formatUnderlyingBias, getTradeUnderlyingBiasSummary } from '@/utils/tradeSemantics';

type TradeHeaderTrade = UserPortfolioTrade | TradingStrategyBacktestTrade;

interface TradeHeaderProps {
    trade: TradeHeaderTrade;
    entity?: Entity | null;
    option?: AssetOption | null;
    options?: readonly EntityOptionMetadata[] | null;
    optionBuyingStrategies?: OptionBuyingStrategy[] | null;
    strategy?: TradingStrategy | null;
}

const calculateTradePerformance = (trade: TradeHeaderTrade): UserPortfolioTradePerformance | null => {
    if (!('backtestId' in trade)) return calculateUserPortfolioTradePerformance(trade);
    if (!isEvaluatedBacktestTrade(trade)) return null;

    const tradeSimulator = new SimulateBacktestTrades({
        startingBalance: 0,
        trades: [trade],
        direction: undefined
    });

    tradeSimulator.simulate();

    const stats = tradeSimulator.getStats();
    const { profit, percentageGains, startingBalance, returnOnRiskCapital, averageRiskCapitalPerDay, tradeSummaries } = stats;
    const fees = trade.transactions.reduce((total, transaction) => total + transaction.fees, 0);
    const tradeSummary = tradeSummaries[0];

    if (!tradeSummary) return null;

    const { openingValue, closingValue, openingTxnType } = tradeSummary;
    const percentageChange = percentageGains[0]?.percentage ?? 0;
    const endingValue = openingTxnType === 'sellToOpen' ? profit : closingValue - openingValue;

    return {
        startingBalance,
        endingValue,
        endingBalance: stats.endingBalance,
        fees,
        profit,
        profitPercentage: percentageChange,
        openingValue,
        returnOnRiskCapital,
        averageRiskCapitalPerDay
    };
};

const formatCurrencyMetric = (value: number | null) => (value === null ? 'Unavailable' : formatCurrency(value));
const formatPercentageMetric = (value: number | null) => (value === null ? 'Unavailable' : formatPercentage(value));
const getPerformanceColorClass = (value: number | null) =>
    value === null ? 'text-zinc-400' : value >= 0 ? 'text-profit-green' : 'text-red-500';

const TradeHeader: React.FC<TradeHeaderProps> = ({ trade, entity, optionBuyingStrategies, option, options, strategy }) => {
    const location = useLocation();
    const isOnIndividualBacktestPage = location.pathname === '/tools/backtestTrades';

    const tradePerformance = calculateTradePerformance(trade);
    const openingValue = tradePerformance?.openingValue ?? null;
    const endingValue = tradePerformance?.endingValue ?? null;
    const fees = tradePerformance?.fees ?? null;
    const profit = tradePerformance?.profit ?? null;
    const profitPercentage = tradePerformance?.profitPercentage ?? null;
    const returnOnRiskCapital = tradePerformance?.returnOnRiskCapital ?? null;
    const averageRiskCapitalPerDay = tradePerformance?.averageRiskCapitalPerDay ?? null;
    const singleOptionMetadata: EntityOptionMetadata[] =
        option && option.assetId === trade.entityId && (trade.entityType === 'Asset' || trade.entityType === 'AssetIndex')
            ? [{ ...option, entityType: trade.entityType }]
            : [];
    const underlyingBiasSummary = getTradeUnderlyingBiasSummary(trade, options ?? singleOptionMetadata);

    // Get the entry date for display
    const entryDate =
        trade.transactions.length > 0
            ? getDate({
                  date: new Date(trade.transactions[0].timestamp * 1000),
                  format: 'readable2'
              })
            : 'N/A';

    return (
        <Card className="mb-4 w-full">
            <h5 className="mb-3">Trade Details</h5>

            {/* Two-row layout for all screen sizes */}
            <div className="bg-[#222222] rounded-lg p-4 space-y-6">
                {/* Top row - Primary information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Trade ID</span>
                        <div className="text-white font-medium">{trade.id}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Entity</span>
                        <div>
                            {entity ? <EntityLink entity={entity} /> : <span className="text-zinc-400">Unknown Entity</span>}
                            {option && entity ? (
                                <div className="mt-1">
                                    <a className="text-zinc-400 text-sm" href={getEntityOptionLink(entity, option.id)}>
                                        {option.description}
                                    </a>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Position side</span>
                        <div className="text-white">{formatPositionSide(trade.direction === 'either' ? null : trade.direction)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Underlying exposure</span>
                        <div className="text-white">{formatUnderlyingBias(underlyingBiasSummary)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Entry Date</span>
                        <div className="text-white">{entryDate}</div>
                    </div>
                </div>

                {/* Individual backtest link for BacktestTrade - only show on portfolio backtest page */}
                {'backtestId' in trade && trade.backtestId && !isOnIndividualBacktestPage && (
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Individual Backtest</span>
                        <div>
                            <Link className="text-blue-400 hover:text-blue-300" to={`/tools/backtestTrades?backtestId=${trade.backtestId}`}>
                                View Individual Backtest Trades
                            </Link>
                        </div>
                    </div>
                )}

                {/* Option strategy if present */}
                {optionBuyingStrategies && optionBuyingStrategies.length > 0 && (
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Option Strategy</span>
                        <div className="text-white">{optionBuyingStrategies.map((s) => s.name).join(', ')}</div>
                    </div>
                )}

                {/* Trading strategy if present */}
                {strategy && (
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Trading Strategy</span>
                        <div className="text-white">
                            <Link className="text-blue-400 hover:text-blue-300" to={`/user/tradingStrategy/${strategy.id}`}>
                                {strategy.name}
                            </Link>
                        </div>
                    </div>
                )}

                {/* Financial metrics */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Opening Value</span>
                        <div className="text-white">{formatCurrencyMetric(openingValue)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Ending Value</span>
                        <div className="text-white">{formatCurrencyMetric(endingValue)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Fees</span>
                        <div className="text-white">{formatCurrencyMetric(fees)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">Profit</span>
                        <div className={`font-medium ${getPerformanceColorClass(profit)}`}>{formatCurrencyMetric(profit)}</div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">% Change</span>
                        <div className={`font-medium ${getPerformanceColorClass(profitPercentage)}`}>
                            {formatPercentageMetric(profitPercentage)}
                        </div>
                    </div>
                    <div>
                        <span className="text-zinc-400 text-xs uppercase">RoRC</span>
                        <div className={`font-medium ${getPerformanceColorClass(returnOnRiskCapital)}`}>
                            {formatPercentageMetric(returnOnRiskCapital)}
                        </div>
                    </div>
                </div>

                {/* Risk capital - separate row */}
                <div>
                    <span className="text-zinc-400 text-xs uppercase">Average Risk Capital Per Day</span>
                    <div className="text-white">{formatCurrencyMetric(averageRiskCapitalPerDay)}</div>
                </div>
            </div>
        </Card>
    );
};

export default TradeHeader;
